import type React from 'react'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useParams } from 'react-router-dom'
import { Actions } from 'Flux/Slice/index.js'
import { isLabInvite, isOrderCompletedNotification, isProjectInvite } from '../../../Model/Invite/IInvite.js'
import { invitationSlice } from '../../../Flux/Slice/Invitation/InvitationSlice.js'
import { useCurrentInvite, useInvites } from '../../../Flux/Selector/index.js'
import { ExtendedLabInviteNotification, ExtendedOrderCompletionInvite, ExtendedProjectInvite } from './ExtendedInvites.js'

export const InviteDetailPage: React.FC = () => {
  const { inviteId } = useParams<{ inviteId: string }>()
  const invites = useInvites()
  const dispatch = useDispatch()
  const invite = useCurrentInvite()

  useEffect(() => {
    if (invites === 'LOADING') {
      dispatch(Actions[invitationSlice.name].getAllInvitesRequest())
    }
  }, [])

  const isReceivedInvite = (id: number) => {
    if (invites === 'LOADING') {
      return undefined
    }
    return (
      invites.received.labInvites?.find((x) => x.id === id) ||
      invites.received.projectInvites?.find((x) => x.id === id) ||
      invites.received.orderCompletedNotifications?.find((x) => x.id === id)
    )
  }

  useEffect(() => {
    if (inviteId) {
      dispatch(Actions[invitationSlice.name].getInviteByIdRequest({ inviteId: parseInt(inviteId, 10) }))
    }
  }, [dispatch, inviteId])

  if (invite === 'LOADING' || invite == undefined) {
    return (
      <div className="container mx-auto p-6">
        <h1 className="text-2xl font-bold mb-6">Invitation Details</h1>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    )
  }
  if (invite === 'ERROR') {
    return (
      <div className="container mx-auto p-6">
        <div className="mb-4">
          <Link to="/invite" className="text-blue-600 hover:text-blue-800">
            &larr; Back to All Invitations
          </Link>
        </div>
        <h1 className="text-2xl font-bold mb-6">Invitation Details</h1>
        <div className="flex justify-center items-center h-64">
          <div className="alert alert-error text-2xl text-white flex justify-center">
            <span>Invite not found</span>
          </div>
        </div>
      </div>
    )
  }

  // Render content based on invite type
  const renderInviteContent = () => {
    const type = isReceivedInvite(invite.id) ? 'RECEIVE' : 'SEND'
    if (isProjectInvite(invite)) {
      return <ExtendedProjectInvite type={type} invite={invite} />
    } else if (isLabInvite(invite)) {
      return <ExtendedLabInviteNotification invite={invite} type={type} />
    } else if (isOrderCompletedNotification(invite)) {
      return <ExtendedOrderCompletionInvite invite={invite} type={type} />
    } else {
      return (
        <div className="bg-white shadow-md rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Unknown Invitation Type</h2>
          <p className="text-gray-500">The type of this invitation could not be determined.</p>
        </div>
      )
    }
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-4">
        <Link to="/invite" className="text-blue-600 hover:text-blue-800">
          &larr; Back to All Invitations
        </Link>
      </div>
      <h1 className="text-2xl font-bold mb-6 text-primary">Invitation Details</h1>
      {renderInviteContent()}
    </div>
  )
}
